// @school-apps/core/lib/pacing — the anti-all-nighter arithmetic (A·2). Alivia's ask: the hub
// knows every assignment and due date, but nothing surfaces "what should I touch today". This is
// PURE calendar arithmetic over already-synced hub data — no AI, no endpoint, no clock of its own
// (the caller passes local today) — so it is never wrong, only early.
//
// Bundled CLIENT-side (subpath export, like srs/fsrs): each app's dashboard "Today" panel renders
// straight from this. Lives in core because all three kids get the panel (A·3) — one owner for
// the pacing rules, three consumers.

/** Days between two date-only ISO strings (YYYY-MM-DD), b - a, as whole calendar days. */
const daysBetween = (a, b) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

/**
 * Compute the "Today" panel's items from open assignments.
 * Rules (settled): done assignments never appear; no due date -> excluded; sorted by due date
 * (overdue naturally first); overdue -> "n day(s) overdue" + do-this-today; due today ->
 * "due today" + do-this-today; the SINGLE nearest upcoming within 7 days -> "start this";
 * everything else is a plain "n days left" listing. Capped at the nearest `cap` items with a
 * `more` count for the "+ n more in Courses" link.
 * @param {Array<{ id: string, courseId: string, title: string, due?: string, done?: boolean }>} assignments
 * @param {string} today - LOCAL date-only ISO (YYYY-MM-DD) — the caller's clock, injectable in tests
 * @param {{ cap?: number }} [opts]
 * @returns {{ items: Array<{ id, courseId, title, due, daysLeft, status: "overdue"|"today"|"start"|"upcoming", label: string, nudge: string|null }>, more: number }}
 */
export function computePacing(assignments, today, { cap = 6 } = {}) {
  const open = (Array.isArray(assignments) ? assignments : [])
    .filter((a) => a && !a.done && typeof a.due === "string" && /^\d{4}-\d{2}-\d{2}$/.test(a.due))
    .sort((a, b) => (a.due < b.due ? -1 : a.due > b.due ? 1 : a.title < b.title ? -1 : 1));

  let startAssigned = false;
  const all = open.map((a) => {
    const daysLeft = daysBetween(today, a.due);
    let status, label, nudge;
    if (daysLeft < 0) {
      status = "overdue";
      label = `${-daysLeft} day${daysLeft === -1 ? "" : "s"} overdue`;
      nudge = "do this today";
    } else if (daysLeft === 0) {
      status = "today";
      label = "due today";
      nudge = "do this today";
    } else if (!startAssigned && daysLeft <= 7) {
      // the ONE nearest upcoming inside a week gets the start-this nudge — a single clear action
      startAssigned = true;
      status = "start";
      label = daysLeft === 1 ? "tomorrow" : `${daysLeft} days left`;
      nudge = "start this";
    } else {
      status = "upcoming";
      label = daysLeft === 1 ? "tomorrow" : `${daysLeft} days left`;
      nudge = null;
    }
    return { id: a.id, courseId: a.courseId, title: a.title, due: a.due, daysLeft, status, label, nudge };
  });

  return { items: all.slice(0, cap), more: Math.max(0, all.length - cap) };
}
